import { getMedia, StoryblokComponent } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import Image from "next/image";
import type { ISbStoryData } from "storyblok";
import { Typography } from "@/components/ui/Typography";
import type { MobileMenuProps } from "@/features/navigation/Header/components/MobileMenu";
import type { ArticlePageStoryblok } from "@/generated/sb/sb-types";
import { Link } from "@/i18n/routing";
import { sbLink } from "@/lib/storyblok/link";

type MegaMenuType = Extract<
	NonNullable<MobileMenuProps["sections"]>[number],
	{ component: "megaMenu" }
>;

export interface SubSectionViewProps {
	sections?: MegaMenuType["sections"];
	stories?: ISbStoryData<ArticlePageStoryblok>[];
	preview: boolean;
}

export const SubSectionView = ({ sections, stories, preview }: SubSectionViewProps) => {
	if (!sections) return null;

	return (
		<>
			{sections.map((section, i) => (
				<div
					key={section._uid}
					className={clsx(
						"pb-grid-section-vertical-lg",
						i !== 0 && "border-t border-border-medium pt-grid-section-vertical-lg",
					)}
				>
					{section.header && (
						<Typography
							className="mb-grid-section-vertical-md text-text-sand-text-4"
							variant="label-sm-reg"
						>
							{section.header}
						</Typography>
					)}
					<div className="flex flex-col gap-grid-component-spacing-2xs">
						{section.links?.map((link) => (
							<StoryblokComponent
								key={link._uid}
								blok={link}
								preview={preview}
								className="text-text-dark-text-1 text-[14px] leading-[150%] py-0.5"
								unstyled
								rightIcon={false}
							/>
						))}
					</div>
				</div>
			))}
			<div className="flex flex-col w-full gap-4 mt-grid-section-vertical-lg">
				{stories?.map((story, i) => {
					const media = story.content.previewImage
						? getMedia(story.content.previewImage)
						: undefined;

					return (
						<Link
							key={i}
							href={sbLink(story.full_slug)?.href ?? story.full_slug}
							className="flex flex-col gap-2"
						>
							<div className="relative w-full" style={{ aspectRatio: "344 / 229" }}>
								{media && (
									<Image src={media.url} alt={media.alt || ""} fill className="object-cover" />
								)}
							</div>
							<p className="font-display leading-[125%] text-[16px]">
								{story.content.previewTitle}
							</p>
						</Link>
					);
				})}
			</div>
		</>
	);
};
